import { ApolloClient, useApolloClient } from '@apollo/client';
import { useQuery } from '@redwoodjs/web';
import { LIST_CAPS } from '@shared/core/lists/listCaps';

import { useEffect, useMemo, useState } from 'react';
import type { DistinctValueOptionsQuery, DistinctValueOptionsQueryVariables } from 'types/graphql';
import { useGridRowsPainted } from '../../shared/gridRowsReadiness';
import type { GridToolbarQuickFilter } from '../GridToolbarV2';

export const GET_DISTINCT_VALUES = gql`
  query DistinctValuesQuery($input: DistinctValuesInput!) {
    distinctValues(input: $input)
  }
`;

export const GET_DISTINCT_VALUE_OPTIONS = gql`
  query DistinctValueOptionsQuery($input: DistinctValuesInput!) {
    distinctValueOptions(input: $input, take: 5000)
  }
`;

// Sentinel value grids substitute for a NULL/blank cell (e.g. `row.product?.grade || '--'`).
// The server's DISTINCT query strips it, so it must be re-added as an option for NULL rows to be selectable.
export const EMPTY_FILTER_VALUE = '--';

// Canonical filter-comparison normalization: case- and whitespace-insensitive.
// Client and server must agree; keep this in sync with the paginated-query filter normalization.
export function normalizeFilterValue(value: unknown): unknown {
  return typeof value === 'string' ? value.trim().toLowerCase() : value;
}

// Membership test for `in`/`notIn` that tolerates case/whitespace drift between distinct options and row values.
export function matchesNormalized(fieldValue: unknown, candidates: Array<string | number | boolean>): boolean {
  const normalizedField = normalizeFilterValue(fieldValue);
  return candidates.some((candidate) => normalizeFilterValue(candidate) === normalizedField);
}

// This query calls custom SELECT DISTINCT queries for a given key
// to add a new key see `api/src/services/distinctValues/distinctValues.ts`

export async function getDistinctValues(client: ApolloClient<object>, key: string) {
  const result = await client.query({ query: GET_DISTINCT_VALUES, variables: { input: { key } } });

  return result.data.distinctValues;
}

// Appends the NULL/blank sentinel option so rows whose value resolves to `EMPTY_FILTER_VALUE` are selectable.
export async function getDistinctValuesWithEmpty(
  client: ApolloClient<object>,
  key: string
): Promise<Array<{ value: string; label: string }>> {
  const values: unknown[] = await getDistinctValues(client, key);
  const options = values.map((v) => ({ value: String(v), label: String(v) }));
  return [...options, { value: EMPTY_FILTER_VALUE, label: '(None)' }];
}

export const DISTINCT_VALUE_OPTIONS_TAKE = LIST_CAPS.distinctValueOptions;

// A full bounded page may be truncated, so it re-reads the complete legacy list (one extra query at the cap).
export async function getDistinctValueOptions(client: ApolloClient<object>, key: string): Promise<string[]> {
  const result = await client.query<DistinctValueOptionsQuery, DistinctValueOptionsQueryVariables>({
    query: GET_DISTINCT_VALUE_OPTIONS,
    variables: { input: { key } },
  });
  const values = result.data.distinctValueOptions;
  return values.length >= DISTINCT_VALUE_OPTIONS_TAKE ? getDistinctValues(client, key) : values;
}

export async function getDistinctValueOptionsWithEmpty(
  client: ApolloClient<object>,
  key: string
): Promise<Array<{ value: string; label: string }>> {
  const values = await getDistinctValueOptions(client, key);
  const options = values.map((v) => ({ value: String(v), label: String(v) }));
  return [...options, { value: EMPTY_FILTER_VALUE, label: '(None)' }];
}

export const GET_QUICK_FILTER_COUNTS = gql`
  query QuickFilterCountsQuery($input: QuickFilterCountInput!) {
    quickFilterCounts(input: $input)
  }
`;

/** Stable identity for the "no badges yet" case, so an absent count never re-renders a toolbar. */
const NO_COUNTS: Readonly<Record<string, number>> = {};

/**
 * Apollo nulls `data` across a variable change (the old value moves to `previousData`), so a
 * new-scope count request arrives empty; this is the local half — a badge can only render a
 * count for a label the scope on screen declares.
 */
export function countsForScope(
  counts: Record<string, number> | undefined,
  quickFilters?: GridToolbarQuickFilter[]
): Readonly<Record<string, number>> {
  if (!counts || !quickFilters?.length) return NO_COUNTS;

  const declared = new Set(quickFilters.map((filter) => filter.label));
  const scoped: Record<string, number> = {};

  for (const [label, count] of Object.entries(counts)) {
    if (declared.has(label)) scoped[label] = count;
  }

  return scoped;
}

/**
 * Quick-filter badge counts — NONCRITICAL data, so it waits for the grid's rows.
 *
 *   rows loading ──╳── no request        (the backend runs one COUNT per badge)
 *   rows painted ────▶ ONE request       (cache-first; a cache hit renders immediately)
 *   request fails ───▶ labels, no badge  (never a fabricated zero, never a broken grid)
 *
 * The hook is UNCONDITIONAL and gates through `skip`, so hook order never moves when a
 * grid starts or stops being eligible. Readiness comes from the hosting `DataGrid`'s own
 * `loading` prop via `GridRowsReadinessProvider`; a toolbar with no provider (the report
 * pages' `FreeToolbarV2`) reads `true` and keeps the eager behaviour it has always had.
 *
 * A missing model or an empty filter list skips, exactly as before — there is nothing to
 * count, and `quickFilterCounts` would only throw `Invalid model name` server-side.
 */
export function useQuickFilterCounts(modelName?: string, quickFilters?: GridToolbarQuickFilter[]) {
  const input = useMemo(() => ({ modelName, quickFilters }), [modelName, quickFilters]);
  const countScopeKey = useMemo(() => JSON.stringify(input), [input]);
  const rowsPainted = useGridRowsPainted(countScopeKey);
  const isEligible = !!modelName && !!quickFilters?.length && rowsPainted;

  const { data } = useQuery(GET_QUICK_FILTER_COUNTS, {
    fetchPolicy: 'cache-first',
    variables: { input },
    skip: !isEligible,
  });

  const counts = data?.quickFilterCounts;

  return useMemo(
    () => (isEligible ? countsForScope(counts, quickFilters) : NO_COUNTS),
    [isEligible, counts, quickFilters]
  );
}

export function useAlertIndicator(filter: GridToolbarQuickFilter) {
  const client = useApolloClient();
  const [alertIndicator, setAlertIndicator] = useState<number | string | null>(null);
  const getAlertValue = filter.getAlertValue;

  if (!getAlertValue) {
    return null;
  }

  const fetchValue = getAlertValue;

  useEffect(() => {
    async function fetchAlertValue() {
      const value = await fetchValue(client);

      setAlertIndicator(value);
    }

    fetchAlertValue();
  }, [client, fetchValue]);

  return alertIndicator;
}
